// Guia "Como usar" (texto 08, C1): "▶ Mostrar na tela".
// As telas marcam os pontos reais (useAlvoGuia / <AlvoGuia>), sem mudar o visual. mostrarNaTela(id) abre a tela certa,
// espera o ponto aparecer, mede e o destaque (components/guia/Destaque.tsx, montado uma vez em app/_layout.tsx)
// mostra a setinha e o balão. Fechar oferece "Voltar ao guia" (mesma página e seção).
import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import type { View } from 'react-native';
import type { Destino } from './conteudo';

// ─── Pontos marcados nas telas ───
type No = { measureInWindow?: (cb: (x: number, y: number, w: number, h: number) => void) => void } | null;
const pontos = new Map<string, Set<NonNullable<No>>>();
const ouvintesPontos = new Set<() => void>();

function registrar(id: string, no: NonNullable<No>) {
  let s = pontos.get(id);
  if (!s) pontos.set(id, (s = new Set()));
  s.add(no);
  ouvintesPontos.forEach(f => f());
}
function tirar(id: string, no: NonNullable<No>) {
  const s = pontos.get(id);
  if (!s) return;
  s.delete(no);
  if (!s.size) pontos.delete(id);
}

/** Marca um elemento da tela como alvo do guia. Uso: const a = useAlvoGuia('x'); <Pressable ref={a.ref} collapsable={false} .../>
 *  O ref é uma função: não muda estilo nem layout. Se o elemento já tem ref, use juntarRefs(a.ref, seuRef). */
export function useAlvoGuia(id: string) {
  const atual = useRef<NonNullable<No> | null>(null);
  const ref = useCallback((no: unknown) => {
    if (atual.current) { tirar(id, atual.current); atual.current = null; }
    if (no && typeof no === 'object') { atual.current = no as NonNullable<No>; registrar(id, atual.current); }
  }, [id]);
  useEffect(() => () => { if (atual.current) tirar(id, atual.current); }, [id]);
  return { ref: ref as (no: View | null) => void, collapsable: false as const };
}

/** Junta várias refs (função ou objeto) numa só. */
export function juntarRefs<T>(...refs: (((x: T | null) => void) | { current: T | null } | null | undefined)[]) {
  return (x: T | null) => { for (const r of refs) { if (typeof r === 'function') r(x); else if (r) r.current = x; } };
}

// ─── Preparar a tela antes de destacar (abrir o 1º fichário, a matéria, a página, o ⋮ do editor...) ───
// Alvo.antes chama prepararTela('estudos', 'abrirMateria'); a tela escuta com usePreparoTela('estudos', acao => ...).
// Se a tela ainda não existe (vai montar agora), o pedido espera alguns segundos por ela.
type Preparo = { tela: string; acao: string; ate: number };
let preparo: Preparo | null = null;
const ouvintesPreparo = new Map<string, Set<(acao: string) => void>>();
export function prepararTela(tela: string, acao: string) {
  const s = ouvintesPreparo.get(tela);
  if (s?.size) { s.forEach(f => f(acao)); return; }
  preparo = { tela, acao, ate: Date.now() + 4000 };
}
/** A tela escuta os pedidos do guia (só abre o que já existe; não cria nada). */
export function usePreparoTela(tela: string, cb: (acao: string) => void) {
  const atual = useRef(cb);
  atual.current = cb;
  useEffect(() => {
    const f = (acao: string) => atual.current(acao);
    let s = ouvintesPreparo.get(tela);
    if (!s) ouvintesPreparo.set(tela, (s = new Set()));
    s.add(f);
    if (preparo?.tela === tela && Date.now() < preparo.ate) { const acao = preparo.acao; setTimeout(() => f(acao), 0); }
    return () => { s.delete(f); };
  }, [tela]);
}

export type Retangulo = { x: number; y: number; w: number; h: number };
const medir = (no: NonNullable<No>) => new Promise<Retangulo | null>(res => {
  try {
    if (!no.measureInWindow) return res(null);
    let feito = false;
    no.measureInWindow((x, y, w, h) => { feito = true; res(w > 0 && h > 0 ? { x, y, w, h } : null); });
    setTimeout(() => { if (!feito) res(null); }, 400);
  } catch { res(null); }
});

/** Procura o ponto na tela por até `ms` (padrão 2,5 s). Escolhe um que esteja visível na janela. */
export async function acharPonto(id: string, janela: { w: number; h: number }, ms = 2500): Promise<Retangulo | null> {
  const fim = Date.now() + ms;
  while (Date.now() < fim) {
    const s = pontos.get(id);
    if (s?.size) {
      const medidas = await Promise.all([...s].map(medir));
      const visivel = medidas.find(r => r && r.x + r.w > 0 && r.y + r.h > 0 && r.x < janela.w && r.y < janela.h);
      if (visivel) return visivel;
      const algum = medidas.find(Boolean);
      if (algum && Date.now() + 200 >= fim) return algum;
    }
    await new Promise(r => setTimeout(r, 150));
  }
  return null;
}

// ─── Pedido de destaque (o Destaque da raiz do app escuta) ───
/** De onde veio o pedido: para o "Voltar ao guia" reabrir a mesma página e seção. */
export type Volta = Destino;
export type Pedido = { alvo: string; texto?: string; volta?: Volta; n: number };

let pedido: Pedido | null = null;
let contador = 0;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach(f => f());
const assinar = (cb: () => void) => { ouvintes.add(cb); return () => { ouvintes.delete(cb); }; };

/** "▶ Mostrar na tela": abre a tela real e destaca o ponto. texto: explicação do balão (o passo); volta: onde voltar no guia. */
export function mostrarNaTela(alvo: string, opcoes?: { texto?: string; volta?: Volta }) {
  pedido = { alvo, texto: opcoes?.texto, volta: opcoes?.volta, n: ++contador };
  avisar();
}
export const usePedidoDestaque = () => useSyncExternalStore(assinar, () => pedido);
export function fecharPedido() { if (pedido) { pedido = null; avisar(); } }

// ─── Voltar ao guia: Ajustes reabre as páginas; a página do guia abre a seção e rola até o passo ───
let pilhaAjustes: string[] | null = null;
const ouvintesPilha = new Set<(p: string[]) => void>();
/** Pede para Ajustes abrir estas páginas (ex.: ['comousar', 'guia:aba:estudos']). Quem chama navega para /ajustes. */
export function pedirPaginasAjustes(p: string[]) {
  pilhaAjustes = p;
  if (ouvintesPilha.size) { const x = p; pilhaAjustes = null; ouvintesPilha.forEach(f => f(x)); }
}
/** Ajustes escuta (e pega o pedido que chegou antes de a tela existir). */
export function ouvirPaginasAjustes(cb: (p: string[]) => void) {
  ouvintesPilha.add(cb);
  if (pilhaAjustes) { const x = pilhaAjustes; pilhaAjustes = null; cb(x); }
  return () => { ouvintesPilha.delete(cb); };
}

let foco: (Destino & { n: number }) | null = null;
const ouvintesFoco = new Set<() => void>();
/** Abrir a seção (e rolar até o passo/pergunta) na página do guia. */
export function pedirFoco(d: Destino) { foco = { ...d, n: ++contador }; ouvintesFoco.forEach(f => f()); }
export const useFoco = () => useSyncExternalStore(cb => { ouvintesFoco.add(cb); return () => { ouvintesFoco.delete(cb); }; }, () => foco);
export function focoUsado(n: number) { if (foco?.n === n) foco = null; }
